import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ConfirmModalComponent, ConfirmModalData } from '../../student/modal/confirm-modal/confirm-modal.component';

/** Anything with editable state that should block leaving the page while dirty. */
export interface UnsavedEditsSource {
  hasUnsavedEdits: () => boolean;
}

/**
 * Registry of components holding unsaved edits, consulted by PendingChangesGuard
 * before a route change. Components register while mounted (and unregister on
 * destroy) so the guard needs no knowledge of where in the view tree they live.
 * Also hosts the shared confirm dialog so callers do not each inject MatDialog.
 */
@Injectable({ providedIn: 'root' })
export class UnsavedEditsService {
  private sources = new Set<UnsavedEditsSource>();

  constructor(private dialog: MatDialog) { }

  /** Registers a source; returns the function that unregisters it. */
  register(source: UnsavedEditsSource): () => void {
    this.sources.add(source);
    return () => { this.sources.delete(source); };
  }

  hasUnsavedEdits(): boolean {
    for (const source of this.sources) {
      if (source.hasUnsavedEdits()) { return true; }
    }
    return false;
  }

  /** True when navigation may proceed: nothing is dirty, or the user chose to leave. */
  canLeave(): Promise<boolean> {
    if (!this.hasUnsavedEdits()) { return Promise.resolve(true); }
    return this.confirm('You have unsaved changes. Leave without saving?', 'Leave');
  }

  /** Yes/no dialog; resolves true only when the user confirms. */
  async confirm(message: string, confirmLabel: string, cancelLabel = 'Keep editing'): Promise<boolean> {
    const data: ConfirmModalData = { message, confirmLabel, cancelLabel };
    const ref = this.dialog.open(ConfirmModalComponent, { data, disableClose: true });
    return (await firstValueFrom(ref.afterClosed())) === true;
  }
}
